import React, { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import { Flex, Card, Grid, Badge, Button, Select, TextField } from "@radix-ui/themes"
import { Button as UIButton } from "../../components/ui/button"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator
} from "../../components/ui/dropdown-menu"
import {
    Search,
    Filter,
    MoreVertical,
    Eye,
    Clock,
    CheckCircle,
    XCircle,
    DollarSign,
    User,
    MapPin,
    Truck
} from "lucide-react"
import { restaurantPortalAPI } from "../../services/restaurantApiClient"
import { useToast } from "../../hooks/use-toast"
import { useLanguage } from "../../contexts/LanguageContext"
import PageHeader from "../../components/ui/dashboard/PageHeader"
import StatCard from "../../components/ui/dashboard/StatCard"
import LoadingSpinner from "../../components/ui/dashboard/LoadingSpinner"
import TurboLogoIcon from "../../components/TurboLogoIcon"

interface DeliveryOrder {
    _id: string
    orderNumber: string
    restaurant: string
    customer?: {
        _id: string
        username?: string
        fullName?: string
        phoneNumber?: string
        email?: string
    }
    customerInfo?: {
        name?: string
        phone?: string
    }
    deliveryAddress?: {
        address?: string
        street?: string
        city?: string
        state?: string
        zipCode?: string
        coordinates?: {
            latitude: number
            longitude: number
        }
        notes?: string
    }
    items: Array<{
        foodItem: {
            _id: string
            name: string
            nameAr?: string
            price: number
        }
        quantity: number
        price: number
        size?: {
            name: string
            nameAr?: string
            price: number
        }
        specialInstructions?: string
    }>
    pricing: {
        subtotal: number
        tax: number
        deliveryFee?: number
        discount?: number
        total: number
    }
    paymentMethod?: string
    paymentStatus?: string
    status: string
    orderSource?: string
    captain?: {
        _id: string
        name: string
        phone?: string
        phoneNumber?: string
    }
    specialInstructions?: string
    estimatedDeliveryTime?: string
    createdAt: string
    updatedAt: string
}

const DeliveryOrdersPage: React.FC = () => {
    const { restaurantId } = useParams()
    const { toast } = useToast()
    const { t, language, dir } = useLanguage()
    const [orders, setOrders] = useState<DeliveryOrder[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState("")
    const [statusFilter, setStatusFilter] = useState("all")

    useEffect(() => {
        fetchDeliveryOrders()
    }, [restaurantId])

    const fetchDeliveryOrders = async () => {
        try {
            setLoading(true)
            const params: any = { orderType: 'delivery' }
            if (searchTerm) params.search = searchTerm
            if (statusFilter && statusFilter !== "all") params.status = statusFilter

            const response = await restaurantPortalAPI.getOrders(restaurantId!, params)
            // Show all delivery orders (from app, phone, web, etc.)
            const deliveryOrders = response.data.orders || []
            setOrders(deliveryOrders)
        } catch (error) {
            console.error("Error fetching delivery orders:", error)
            toast({
                title: t("error"),
                description: t("failedToLoadDeliveryOrders"),
                variant: "destructive",
            })
        } finally {
            setLoading(false)
        }
    }

    const handleStatusUpdate = async (orderId: string, newStatus: string) => {
        try {
            await restaurantPortalAPI.updateOrderStatus(orderId, { status: newStatus })
            toast({
                title: t("success"),
                description: t("orderStatusUpdated"),
            })
            fetchDeliveryOrders()
        } catch (error) {
            toast({
                title: t("error"),
                description: t("failedToUpdateStatus"),
                variant: "destructive",
            })
        }
    }

    const getStatusBadgeColor = (status: string): "orange" | "blue" | "yellow" | "green" | "red" | "gray" => {
        switch (status) {
            case "pending": return "orange"
            case "confirmed": return "blue"
            case "preparing": return "yellow"
            case "ready": return "green"
            case "picked-up": return "blue"
            case "on-the-way": return "blue"
            case "delivered": return "green"
            case "cancelled": return "red"
            default: return "gray"
        }
    }

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "SDG",
        }).format(amount)
    }

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString()
    }

    const formatAddress = (order: DeliveryOrder): string => {
        const addr = order.deliveryAddress
        if (!addr) return t("noAddress")
        if (addr.address) return addr.address
        const parts = [addr.street, addr.city, addr.state, addr.zipCode].filter(Boolean)
        return parts.join(', ') || t("noAddress")
    }

    const getStatusIcon = (status: string) => {
        switch (status) {
            case "pending": return <Clock className="h-4 w-4" />
            case "confirmed": return <CheckCircle className="h-4 w-4" />
            case "preparing": return <Clock className="h-4 w-4" />
            case "ready": return <CheckCircle className="h-4 w-4" />
            case "picked-up": return <Truck className="h-4 w-4" />
            case "on-the-way": return <Truck className="h-4 w-4" />
            case "delivered": return <CheckCircle className="h-4 w-4" />
            case "cancelled": return <XCircle className="h-4 w-4" />
            default: return <Clock className="h-4 w-4" />
        }
    }

    const getCustomerName = (order: DeliveryOrder): string => {
        return order.customer?.fullName || order.customer?.username || order.customerInfo?.name || t("customer")
    }

    const getCustomerPhone = (order: DeliveryOrder): string => {
        return order.customer?.phoneNumber || order.customerInfo?.phone || ''
    }

    const getOrderSourceBadge = (source?: string) => {
        switch (source) {
            case 'phone': return { label: t("phoneOrder"), color: "blue" as const }
            case 'app': return { label: t("appOrder"), color: "green" as const }
            case 'web': return { label: t("webOrder"), color: "purple" as const }
            default: return { label: t("delivery"), color: "gray" as const }
        }
    }

    if (loading) {
        return <LoadingSpinner size="3" text={t("loadingDeliveryOrders")} />
    }

    return (
        <div className="space-y-6" dir={dir}>
            <PageHeader
                title={t("deliveryOrders")}
                description={t("manageDeliveryOrders")}
                action={
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                        <Truck size={20} />
                        <span className="text-sm font-medium">{t("allDeliverySources")}</span>
                    </div>
                }
            />

            {/* Filters */}
            <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                <div className="p-4">
                    <Grid columns={{ initial: "1", md: "3" }} gap="4">
                        <TextField.Root
                            placeholder={t("searchOrders")}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        >
                            <TextField.Slot>
                                <Search size={16} />
                            </TextField.Slot>
                        </TextField.Root>

                        <Select.Root value={statusFilter} onValueChange={setStatusFilter}>
                            <Select.Trigger style={{ width: "100%" }} placeholder={t("filterByStatus")} />
                            <Select.Content>
                                <Select.Item value="all">{t("allStatus")}</Select.Item>
                                <Select.Item value="pending">{t("pending")}</Select.Item>
                                <Select.Item value="confirmed">{t("confirmed")}</Select.Item>
                                <Select.Item value="preparing">{t("preparing")}</Select.Item>
                                <Select.Item value="ready">{t("ready")}</Select.Item>
                                <Select.Item value="picked-up">{t("pickedUp")}</Select.Item>
                                <Select.Item value="on-the-way">{t("onTheWay")}</Select.Item>
                                <Select.Item value="delivered">{t("delivered")}</Select.Item>
                                <Select.Item value="cancelled">{t("cancelled")}</Select.Item>
                            </Select.Content>
                        </Select.Root>

                        <Button onClick={fetchDeliveryOrders} variant="outline">
                            <Filter size={16} className={language === 'ar' ? 'ml-2' : 'mr-2'} />
                            {t("applyFilters")}
                        </Button>
                    </Grid>
                </div>
            </Card>

            {/* Orders List */}
            {orders.length === 0 ? (
                <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                    <div className="text-center py-12">
                        <TurboLogoIcon size={48} className="text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t("noDeliveryOrdersFound")}</h3>
                        <p className="text-gray-600 dark:text-gray-400 mt-2">
                            {searchTerm || statusFilter !== "all"
                                ? t("tryAdjustingFilters")
                                : t("deliveryOrdersWillAppear")
                            }
                        </p>
                    </div>
                </Card>
            ) : (
                <Flex direction="column" gap="4">
                    {orders.map((order) => (
                        <Card key={order._id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 overflow-hidden">
                            {/* Header Row */}
                            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                                <div className="flex items-center gap-3 flex-wrap">
                                    <TurboLogoIcon size={20} className="text-red-600" />
                                    <span className="font-bold text-gray-900 dark:text-white">#{order.orderNumber}</span>
                                    <Badge color={getStatusBadgeColor(order.status)} size="2">
                                        <span className="flex items-center gap-1">
                                            {getStatusIcon(order.status)}
                                            {t(order.status)}
                                        </span>
                                    </Badge>
                                    {order.orderSource && (
                                        <Badge color={getOrderSourceBadge(order.orderSource).color} size="1" variant="soft">
                                            {getOrderSourceBadge(order.orderSource).label}
                                        </Badge>
                                    )}
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className={`text-${language === 'ar' ? 'left' : 'right'}`}>
                                        <div className="text-lg font-bold text-gray-900 dark:text-white">
                                            {formatCurrency(order.pricing.total)}
                                        </div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {formatDate(order.createdAt)}
                                        </p>
                                    </div>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <UIButton variant="outline" size="icon" className="h-9 w-9">
                                                <MoreVertical className="h-4 w-4" />
                                            </UIButton>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-48">
                                            <DropdownMenuLabel>{t("actions")}</DropdownMenuLabel>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem className="cursor-pointer">
                                                <Eye className="h-4 w-4" />
                                                {t("viewDetails")}
                                            </DropdownMenuItem>
                                            {order.status === "pending" && (
                                                <DropdownMenuItem
                                                    className="cursor-pointer text-green-600 focus:text-green-600"
                                                    onClick={() => handleStatusUpdate(order._id, "confirmed")}
                                                >
                                                    <CheckCircle className="h-4 w-4" />
                                                    {t("confirmOrder")}
                                                </DropdownMenuItem>
                                            )}
                                            {order.status === "confirmed" && (
                                                <DropdownMenuItem
                                                    className="cursor-pointer text-blue-600 focus:text-blue-600"
                                                    onClick={() => handleStatusUpdate(order._id, "preparing")}
                                                >
                                                    <Clock className="h-4 w-4" />
                                                    {t("startPreparing")}
                                                </DropdownMenuItem>
                                            )}
                                            {order.status === "preparing" && (
                                                <DropdownMenuItem
                                                    className="cursor-pointer text-green-600 focus:text-green-600"
                                                    onClick={() => handleStatusUpdate(order._id, "ready")}
                                                >
                                                    <CheckCircle className="h-4 w-4" />
                                                    {t("markReady")}
                                                </DropdownMenuItem>
                                            )}
                                            {!["delivered", "cancelled"].includes(order.status) && (
                                                <>
                                                    <DropdownMenuSeparator />
                                                    <DropdownMenuItem
                                                        className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-900/20"
                                                        onClick={() => handleStatusUpdate(order._id, "cancelled")}
                                                    >
                                                        <XCircle className="h-4 w-4" />
                                                        {t("cancelOrder")}
                                                    </DropdownMenuItem>
                                                </>
                                            )}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            </div>

                            {/* Customer & Delivery Info */}
                            <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50/50 dark:bg-gray-800/50">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                                        <User className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-900 dark:text-white">{getCustomerName(order)}</p>
                                        {getCustomerPhone(order) && (
                                            <p className="text-sm text-gray-500 dark:text-gray-400" dir="ltr">{getCustomerPhone(order)}</p>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                                        <MapPin className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{formatAddress(order)}</p>
                                        {order.captain ? (
                                            <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1 mt-1">
                                                <Truck className="h-3.5 w-3.5" />
                                                {order.captain.name}
                                            </p>
                                        ) : (
                                            <p className="text-sm text-orange-600 dark:text-orange-400 mt-1">{t("awaitingDriver")}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Order Items */}
                            <div className="px-5 py-4">
                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        {t("orderItems")} ({order.items.length})
                                    </span>
                                    <span className="text-sm text-gray-500 dark:text-gray-400">
                                        {order.items.reduce((sum, item) => sum + item.quantity, 0)} {t("items")}
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {order.items.slice(0, 4).map((item, index) => {
                                        const itemName = language === 'ar' && item.foodItem.nameAr
                                            ? item.foodItem.nameAr
                                            : item.foodItem.name
                                        const sizeName = item.size
                                            ? (language === 'ar' && item.size.nameAr ? item.size.nameAr : item.size.name)
                                            : ''
                                        return (
                                            <div key={index} className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm">
                                                <span className="font-medium text-gray-900 dark:text-white">{itemName}</span>
                                                {sizeName && <span className="text-red-600 mx-1">({sizeName})</span>}
                                                <span className="text-gray-500 dark:text-gray-400 ml-1">
                                                    {item.quantity}x {formatCurrency(item.size?.price || item.price)}
                                                </span>
                                            </div>
                                        )
                                    })}
                                    {order.items.length > 4 && (
                                        <div className="bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-2 text-sm text-gray-500 dark:text-gray-400 flex items-center">
                                            +{order.items.length - 4} {t("more")}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Special Instructions */}
                            {order.specialInstructions && (
                                <div className="px-5 py-3 bg-yellow-50 dark:bg-yellow-900/20 border-t border-yellow-100 dark:border-yellow-900/30">
                                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                                        <span className="font-medium">{t("note")}:</span> {order.specialInstructions}
                                    </p>
                                </div>
                            )}
                        </Card>
                    ))}
                </Flex>
            )}

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <StatCard
                    title={t("totalDeliveryOrders")}
                    value={orders.length}
                    icon={Truck}
                    color="bg-red-500"
                    subtitle={t("fromTurboApp")}
                />
                <StatCard
                    title={t("pending")}
                    value={orders.filter(o => o.status === "pending").length}
                    icon={Clock}
                    color="bg-orange-500"
                    subtitle={t("awaitingConfirmation")}
                />
                <StatCard
                    title={t("delivered")}
                    value={orders.filter(o => o.status === "delivered").length}
                    icon={CheckCircle}
                    color="bg-green-500"
                    subtitle={t("successfullyDelivered")}
                />
                <StatCard
                    title={t("totalRevenue")}
                    value={formatCurrency(orders.reduce((sum, order) => sum + order.pricing.total, 0))}
                    icon={DollarSign}
                    color="bg-purple-500"
                    subtitle={t("fromDeliveryOrders")}
                />
            </div>
        </div>
    )
}

export default DeliveryOrdersPage
